import Joi from 'joi'
import clsx from 'clsx'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import Checkbox from '../../../../../../UI/Checkbox'

import SettingHint from '../SettingHint'

import { mapCompanyTypeToName } from '../../../../../../shared/helpers'
import {
	COMPANY_BOUND_ROLES,
	REQUEST_CHANNEL_COMPANY_TYPES,
	REQUEST_CHANNEL_ROLES,
	readRequestChannelConfig
} from '../../../../../../shared/requestChannel'

import styles from '../style.module.scss'
import config from '../RequirementsConfig/style.module.scss'

/*
 * „Wer darf eine Anfrage stellen?" — der Anfragen-Kanal je Rolle und
 * Unternehmensform.
 *
 * Bis hierher stand der Hilfe-Knopf allen drei Endnutzer-Rollen offen. Das war
 * für den Start richtig, hält aber der Praxis nicht stand: In einer Junior
 * Basic Company soll oft die Lehrkraft der Weg sein und nicht die Schüler:in —
 * dieselbe Überlegung wie beim Upload durch die Lehrkraft. Statt das im Code
 * zu entscheiden, steht es hier.
 *
 * Der Startwert ist **alles offen** und damit genau der Stand von vorher: Eine
 * Einstellung, die beim Erscheinen etwas zusperrt, wäre eine Änderung, die
 * niemand eingestellt hat.
 *
 * Warum der Schalter neben der Liste steht und nicht nur die Liste da ist: Ein
 * ausgeschalteter Schalter sagt „diese Rolle nicht", drei leere Kästchen sagen
 * dasselbe, sehen aber aus wie ein vergessenes Formular.
 */

const ROLLEN_TEXTE = {
	STUDENT: {
		titel: 'Schüler:innen',
		hinweis: 'Gemessen wird die Junior Company, in der die Schüler:in beschäftigt ist.'
	},
	TEACHER: {
		titel: 'Lehrkräfte',
		hinweis:
			'Eine Lehrkraft betreut oft mehrere Junior Companies. Eine freigegebene Unternehmensform genügt — sonst verlöre sie den Weg wegen der einen JBC neben drei JC.'
	},
	SUPPORTER: {
		titel: 'Unterstützer:innen',
		hinweis: 'Unterstützer:innen gehören zu keiner Junior Company — hier gibt es nur den Schalter.'
	}
}

const rolleSchema = Joi.object().keys({
	active: Joi.boolean(),
	companyTypes: Joi.array().items(Joi.string()).default([]),
	withoutCompany: Joi.boolean()
})

const schema = Joi.object({
	requestChannel: Joi.object().keys(
		REQUEST_CHANNEL_ROLES.reduce((acc, role) => ({ ...acc, [role]: rolleSchema }), {})
	)
})

function RequestChannel(props) {
	const { t } = useTranslation()
	const { register, reset, watch, handleSubmit } = useForm({ resolver: joiResolver(schema) })

	const stand = watch('requestChannel')

	const onSubmit = form => {
		const value = REQUEST_CHANNEL_ROLES.reduce((acc, role) => {
			const eintrag = form.requestChannel?.[role]
			const gebunden = COMPANY_BOUND_ROLES.includes(role)

			return {
				...acc,
				[role]: gebunden
					? {
							active: !!eintrag?.active,
							companyTypes: eintrag?.companyTypes || [],
							withoutCompany: !!eintrag?.withoutCompany
					  }
					: { active: !!eintrag?.active }
			}
		}, {})

		props.updateOptions({ name: 'requestChannel', value }).then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		reset({ requestChannel: readRequestChannelConfig(props.form) })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="requestChannel" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Wer darf eine Anfrage stellen?</h3>
				<SettingHint
					example="Beispiel: bei den Schüler:innen JC und JCC angehakt, JBC nicht — dann stellt in einer Junior Basic Company die Lehrkraft die Anfrage, die Schüler:innen sehen den Hilfe-Knopf dort gar nicht erst."
					warning="Eingeschaltet ohne angehakte Unternehmensform wirkt nirgends — dann kann diese Rolle nirgends fragen. Wer den Weg verliert, bekommt keinen Hinweis, sondern sieht den Knopf einfach nicht; die Änderung greift bei der nächsten Anmeldung bzw. beim nächsten Neuladen. Bereits gestellte Anfragen bleiben in jedem Fall bestehen und lassen sich weiter beantworten, bestätigen und nachhaken."
				>
					Steuert den Hilfe-Knopf unten rechts: Wer ihn sieht, kann eine Anfrage an die zuständige
					Landesbetreuung stellen. Eingestellt wird das je Rolle und — bei Schüler:innen und Lehrkräften — je
					Unternehmensform, denn dieselbe Rolle bedeutet in einer Junior Basic Company etwas anderes als in
					einer Junior Company. Der Anlass ist derselbe wie beim Upload durch die Lehrkraft: In Junior Basic
					Companies soll häufig die Lehrkraft der Weg zur Betreuung sein und nicht die Schüler:in. Im
					Startwert ist alles offen; das entspricht dem bisherigen Verhalten. Wer noch zu keiner Junior
					Company gehört — zu Schuljahresbeginn der Normalfall —, hat keine Unternehmensform, an der sich die
					Freigabe messen ließe; darüber entscheidet je Rolle der Haken „auch ohne Junior Company“: angehakt
					darf diese Person fragen, abgehakt nicht, bis sie einer Junior Company angehört. Wo eine Rolle
					gesperrt ist, blendet die Anwendung den Hilfe-Knopf aus und weist ein trotzdem abgeschicktes
					Formular am Server ab. Nicht berührt ist die Verwaltung: Administrator:innen legen ihre
					Anforderungen weiterhin im Menüpunkt „Anforderungen" an. Ebenfalls nicht berührt sind die eigenen
					Anfragen von früher — sie bleiben unter „Meine Anfragen" erreichbar, und Mitteilungen dazu führen
					weiterhin hinein.
				</SettingHint>
				<div className={styles.sectionContent}>
					{REQUEST_CHANNEL_ROLES.map((role, index) => {
						const texte = ROLLEN_TEXTE[role]
						const eintrag = stand?.[role]
						const gebunden = COMPANY_BOUND_ROLES.includes(role)
						// Die Trennlinie steht zwischen den Rollen, nicht hinter der
						// letzten — darunter kommt schon der Speichern-Knopf.
						const letzte = index === REQUEST_CHANNEL_ROLES.length - 1

						return (
							<div key={role} className={clsx(!letzte && styles.miniSection)}>
								<h4 className={styles.miniSectionTitle}>{texte.titel}</h4>
								<p className={styles.hintLine}>{texte.hinweis}</p>

								<label className={clsx(config.switch, styles.field)}>
									<input type="checkbox" {...register(`requestChannel.${role}.active`)} />
									<span className={config.track} aria-hidden="true" />
									<span className={config.state}>
										{eintrag?.active ? t('activated') : t('deactivated')}
									</span>
								</label>

								{/*
								 * Die Unternehmensformen stehen nur da, solange die
								 * Rolle überhaupt fragen darf. Eine Liste unter einem
								 * ausgeschalteten Schalter sähe aus, als bewirke sie
								 * noch etwas.
								 */}
								{gebunden && eintrag?.active && (
									<>
										<h5 className={styles.subTitle}>Unternehmensformen</h5>
										<div className={styles.choiceRow}>
											{REQUEST_CHANNEL_COMPANY_TYPES.map(type => (
												<Checkbox
													key={type}
													label={`${type} — ${mapCompanyTypeToName(type)}`}
													value={type}
													register={register(`requestChannel.${role}.companyTypes`)}
												/>
											))}
										</div>

										<div className={styles.choiceRow}>
											<Checkbox
												label="auch ohne Junior Company"
												register={register(`requestChannel.${role}.withoutCompany`)}
											/>
										</div>
										{/*
										 * Kurz gehalten, weil derselbe Haken zweimal
										 * dasteht: Was dahintersteckt, erklärt der
										 * Text oben an der Einstellung.
										 */}
										<p className={styles.hintLine}>
											Betrifft alle, die (noch) zu keiner Junior Company gehören — zu
											Schuljahresbeginn der Normalfall.
										</p>
									</>
								)}
							</div>
						)
					})}

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

RequestChannel.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}

export default RequestChannel
